import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import CustomModal from '@/components/CustomModal';
import { RadioButtonGroup } from '@/components/RadioButton';
import Select from '@/components/Select';
import { SCHOOL_STUDENTS_STATUS_OPTIONS } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import { getLessonStudentDropdown } from '@/service/lessonService';

export interface ILessonDetailsFilters {
  studentName: string | string[];
  status: string;
}

interface IDropdownItem {
  id: string;
  name: string;
}

interface IDetailsFilterModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  initialFilters?: ILessonDetailsFilters | null;
  onApplyFilters?: (filters: ILessonDetailsFilters) => void;
}

const DetailsFilterModal = ({
  isOpen,
  setIsOpen,
  initialFilters,
  onApplyFilters,
}: IDetailsFilterModalProps) => {
  const { formatText } = useFormatText();
  const params = useParams();

  const [studentOptions, setStudentOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isStudentsLoading, setIsStudentsLoading] = useState(false);

  const [studentName, setStudentName] = useState<string | string[]>(
    initialFilters?.studentName || []
  );

  const [status, setStatus] = useState<string>(initialFilters?.status || '');

  useEffect(() => {
    const fetchStudents = async () => {
      setIsStudentsLoading(true);
      try {
        const lessonId = params.id;
        if (!lessonId) return;
        const res = await getLessonStudentDropdown([], false, [lessonId]);
        if (res?.status && res?.data) {
          setStudentOptions(
            res.data.map((item: IDropdownItem) => ({
              value: item.id,
              label: item.name,
            }))
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsStudentsLoading(false);
      }
    };

    fetchStudents();
  }, [params.id]);

  const handleClearFilters = () => {
    setStudentName([]);
    setStatus('');
    onApplyFilters?.({
      studentName: [],
      status: '',
    });
    setIsOpen(false);
  };

  const handleApplyFilters = () => {
    onApplyFilters?.({
      studentName,
      status,
    });
    setIsOpen(false);
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={setIsOpen}
      position="right"
      isHeader
      heading={formatText('lessons.details-filter.title')}
      isFooter
      cancelButtonProps={{
        variant: 'link',
        label: formatText('common.clear-filters'),
        onClick: handleClearFilters,
      }}
      confirmButtonProps={{
        label: formatText('common.show-results'),
        onClick: handleApplyFilters,
        variant: 'primary',
      }}
    >
      <div className="flex flex-col gap-6 w-full">
        <Select
          name={formatText('lessons.details-filter.student-name')}
          options={studentOptions}
          selected={studentName}
          onChange={setStudentName}
          placeholder={formatText(
            'lessons.details-filter.student-name-placeholder'
          )}
          className="w-full"
          isMulti
          isClearable
          isLoading={isStudentsLoading}
        />

        <RadioButtonGroup
          name="status"
          label={formatText('lessons.details-filter.status')}
          options={SCHOOL_STUDENTS_STATUS_OPTIONS}
          value={status}
          onChange={setStatus}
          direction="row"
          gap="gap-8"
          formatLabel={formatText}
        />
      </div>
    </CustomModal>
  );
};

export default DetailsFilterModal;
